import {useState} from "react";
import {FormProvider, useForm} from "react-hook-form";

import Button from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {RadioField} from "@/components/form/fields/RadioField/RadioField.tsx";
import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {IntegrationModules} from "@/pages/integrations/integrations.types.ts";

type TestEvent = "test" | "alert_raised" | "alert_cleared";

type TestIntegrationFormValues = {
  event: TestEvent;
};

const EVENT_OPTIONS: Array<{value: TestEvent; label: string}> = [
  {value: "test", label: "Generic test"},
  {value: "alert_raised", label: "Simulate DOWN alert"},
  {value: "alert_cleared", label: "Simulate UP alert"},
];

type SubmissionFormProps = {
  hasResult: boolean;
  showEventSelector: boolean;
  pending: boolean;
};

const SubmissionForm = ({hasResult, showEventSelector, pending}: SubmissionFormProps) => (
  <>
    <div className="row mb-3">
      <div className="col">
        Click the button below to send a test notification to this integration and verify it was sent successfully.
      </div>
    </div>
    {showEventSelector && (
      <div className="mb-3">
        <div className="col-form-label col-12 pb-1 pulseTextBodySmallBold">Test type:</div>
        {EVENT_OPTIONS.map((option) => (
          <RadioField key={option.value} name="event" value={option.value} label={option.label} />
        ))}
      </div>
    )}
    <div className="row mb-4">
      <div className="col">
        <Button type="submit" variant="secondary" disabled={pending}>
          {pending ? "Running..." : !hasResult ? "Start Test" : "Restart Test"}
        </Button>
      </div>
    </div>
  </>
);

type TestResult = {
  success: boolean;
  error: string;
};

type TestResultDisplayProps = {
  result: TestResult | null;
  showDeliveryCheck: boolean;
};

const TestResultDisplay = ({result, showDeliveryCheck}: TestResultDisplayProps) => (
  <Card variant="kpi" className="px-1 pb-0">
    <ol>
      <li>Sending request... {result && <span className="text-success">OK</span>}</li>
      {result && (
        <li>
          Monitoring for errors...&nbsp;
          {result.success && <span className="text-success">No errors detected</span>}
          {!result.success && <span className="text-danger">Errors detected</span>}
        </li>
      )}
      {result?.success && showDeliveryCheck && (
        <li className="text-warning">Check that the message arrived on each phone number.</li>
      )}
      {result && !result.success && (
        <li>Please review the error below and adjust the integration settings accordingly:</li>
      )}
    </ol>
    {result && !result.success && (
      <Card variant="integrationError" className="m-3 p-0">
        <pre className="m-0 p-3 overflow-x-auto">{result.error}</pre>
      </Card>
    )}
  </Card>
);

type TestIntegrationFormProps = {
  integration: IntegrationFormData;
};

export const TestIntegrationForm = ({integration}: TestIntegrationFormProps) => {
  const [result, setResult] = useState<TestResult | null>(null);

  const showEventSelector = integration.module === IntegrationModules.SLACK;
  const showDeliveryCheck = integration.module === IntegrationModules.WHATSAPP;

  const methods = useForm<TestIntegrationFormValues>({
    defaultValues: {event: "test"},
  });

  const onSubmit = async ({event}: TestIntegrationFormValues) => {
    const effectiveEvent = showEventSelector ? event : "test";

    const response = await fetch(`${URL_ORIGINAL_WEBSITE}/integrations/manage/ajax/test`, {
      method: "POST",
      credentials: "include",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({...integration, event: effectiveEvent}),
    });

    setResult(await response.json());
  };

  const pending = methods.formState.isSubmitting;

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <SubmissionForm hasResult={!!result} showEventSelector={showEventSelector} pending={pending} />
        {(pending || !!result) && (
          <TestResultDisplay result={pending ? null : result} showDeliveryCheck={showDeliveryCheck} />
        )}
      </form>
    </FormProvider>
  );
};
